import { useState } from "react";
import { motion } from "framer-motion";
import {
  Menu,
  X,
  Home,
  Zap,
  Building2,
  TrendingUp,
  Users,
  BookOpen,
  Cog,
  LogOut,
  Search,
  Bell,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ThemeToggle from "@/components/theme-toggle";
import Feed from "./Feed";
import Threads from "./Community/Threads";
import Polls from "./Community/Polls";
import Spaces from "./Community/Spaces";
import Library from "./Library";
import Automations from "./Automations";
import LiveCenter from "./LiveCenter";
import CompanyHub from "./Companies/CompanyHub";
import Markets from "./Markets";

type NavTab = "home" | "live" | "companies" | "markets" | "community-threads" | "community-polls" | "community-spaces" | "library" | "automations";

const navItems = [
  { id: "home", label: "Home", icon: Home },
  { id: "live", label: "Live", icon: Zap },
  { id: "companies", label: "Companies", icon: Building2 },
  { id: "markets", label: "Markets", icon: TrendingUp },
  { id: "community", label: "Community", icon: Users, submenu: true },
  { id: "library", label: "Library", icon: BookOpen },
  { id: "automations", label: "Automations", icon: Cog },
];

export default function SteerCode() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<NavTab>("home");
  const [communityTab, setCommunityTab] = useState("threads");

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 },
    },
  };

  const renderContent = () => {
    switch (activeTab) {
      case "home":
        return <Feed />;
      case "live":
        return <LiveCenter />;
      case "companies":
        return <CompanyHub />;
      case "markets":
        return <Markets />;
      case "community-threads":
        return <Threads />;
      case "community-polls":
        return <Polls />;
      case "community-spaces":
        return <Spaces />;
      case "library":
        return <Library />;
      case "automations":
        return <Automations />;
      default:
        return <Feed />;
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <motion.div
        className={`border-r border-border transition-all duration-300 ${
          sidebarOpen ? "w-64" : "w-20"
        } hidden md:block bg-background sticky top-0 h-screen overflow-y-auto`}
        animate={{ width: sidebarOpen ? "16rem" : "5rem" }}
      >
        <div className="p-6 border-b border-border flex items-center justify-between">
          {sidebarOpen && <h1 className="font-bold text-lg tracking-tight">SteerCode</h1>}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1 hover:bg-card rounded transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <div key={item.id}>
              <motion.button
                whileHover={{ x: 4 }}
                onClick={() => {
                  if (item.id === "community") {
                    setActiveTab("community-threads");
                  } else {
                    setActiveTab(item.id as NavTab);
                  }
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded transition-colors text-left ${
                  activeTab.startsWith(item.id)
                    ? "bg-card border-l-2 border-foreground"
                    : "hover:bg-card/50"
                }`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && <span className="text-sm">{item.label}</span>}
              </motion.button>

              {/* Community Submenu */}
              {sidebarOpen && item.id === "community" && activeTab.startsWith("community") && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="ml-4 space-y-1 mt-2"
                >
                  {["Threads", "Polls", "Spaces"].map((sub) => (
                    <button
                      key={sub}
                      onClick={() => setActiveTab(`community-${sub.toLowerCase()}` as NavTab)}
                      className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors ${
                        communityTab === sub.toLowerCase()
                          ? "text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {sub}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>
          ))}
        </nav>

        <div className="border-t border-border p-4 mt-auto space-y-2">
          <Button variant="outline" className="w-full border-border text-sm justify-start">
            <LogOut className="h-4 w-4 mr-2" />
            {sidebarOpen && "Sign Out"}
          </Button>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-40">
          <div className="px-4 py-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 md:hidden">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              <h1 className="font-bold text-lg">SteerCode</h1>
            </div>

            <div className="hidden md:flex flex-1 max-w-md">
              <div className="relative w-full">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search updates, threads, events..."
                  className="pl-10 bg-card border-border"
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                className="relative p-2 hover:bg-card rounded transition-colors"
              >
                <Bell className="h-5 w-5" />
                <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
              </motion.button>
              <ThemeToggle />
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          <motion.div
            className="px-4 py-8 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {renderContent()}
          </motion.div>
        </div>
      </div>
    </div>
  );
}